import { Frustum, InstancedBufferAttribute, Matrix4, Object3D, Vector3 } from 'three';
import { threeJsVariables } from '../../../settings';

const DEFAULT_SETTINGS = {
	offset: 0,
	autoUpdate: true
};
const _position = new Vector3();
const _frustum = new Frustum();
const _camMatrix = new Matrix4();

export class InstanceLOD extends Object3D {
	static scopes = [];
	
	constructor () {
		super();
		const params = config.threejs.instanceLOD || DEFAULT_SETTINGS;
		this.instancesLevels = [];
		this.matrices = [];
		this.levelsRange = [];
		this.minLevel = null;
		this.maxLevel = null;
		this.count = 0;
		this.frustumOffset = params.offset;
		this.isLOD = true;
		this.isInstanceLODObject = true;
		this.autoUpdate = params.autoUpdate;
		this.frustumCulled = false;
		this.matrixAutoUpdate = false;
		this.manualLevelSet = false;
		
		if (!this.autoUpdate) {
			InstanceLOD.scopes.push(this);
		}
	}
	
	addLevel( object, distance = 0 ) {
		object.isInstanceLOD = true;
		
		if (!this.matrices.length) {
			for (let i = 0; i < object.count; i ++) {
				const matrix = new Matrix4();
				object.getMatrixAt(i,matrix);
				this.matrices.push(matrix);
				this.count++;
			}
		}
		
		const data = {
			mesh: object,
			count: 0,
			matrix4: [],
			distance
		};
		
		this.instancesLevels.push(data);
		this.levelsRange.push(distance);
		this.levelsRange = [...new Set(this.levelsRange)];
		this.minLevel = Math.min(...this.levelsRange);
		this.maxLevel = Math.max(...this.levelsRange);
		
		this.add( object );
		
		if (!this.autoUpdate) this.update(threeJsVariables.perspectiveCamera);
		
		return this;
	}
	
	clamp(num,min,max) {
		return Math.min(Math.max(num, min), max);
	}
	
	getMatrixAt( index ) {
		return this.matrices[index].clone();
	}
	
	setMatrixAt( index, matrix ) {
		this.matrices[index] = matrix.clone();
	}
	
	merge(distance,matrix) {
		const clamped = this.clamp(distance,this.minLevel,this.maxLevel);
		
		for (let i = 0; i < this.instancesLevels.length; i++) {
			if (this.instancesLevels[i].distance >= clamped) {
				this.instancesLevels[i].count++;
				this.instancesLevels[i].matrix4.push(matrix);
				break;
			}
		}
	}
	
	update(camera) {
		let distance;
		
		//Clear
		for (let i = 0; i < this.instancesLevels.length; i++) {
			this.instancesLevels[i].count = 0;
			this.instancesLevels[i].matrix4 = [];
		}
		
		_camMatrix.multiplyMatrices(camera.projectionMatrix,camera.matrixWorldInverse);
		_frustum.setFromProjectionMatrix(_camMatrix);
		
		if (this.frustumOffset > 0) {
			
			for (let f = 0; f < _frustum.planes.length; f ++) {
				_frustum.planes[f].constant += this.frustumOffset;
			}
		}
		
		for (let i = 0; i < this.matrices.length; i++) {
			
			_position.set(
				this.matrices[i].elements[12],
				this.matrices[i].elements[13],
				this.matrices[i].elements[14]
			).applyMatrix4(this.matrixWorld);
			
			if (_frustum.containsPoint(_position)) {
				distance = _position.distanceTo(camera.position);
				this.merge(distance,new Matrix4().copy(this.matrices[i]));
			}
		}
		
		
		for (let i = 0; i < this.instancesLevels.length; i++) {
			this.children[i].count = this.instancesLevels[i].count;
			this.children[i].instanceMatrix = new InstancedBufferAttribute( new Float32Array( this.instancesLevels[i].count * 16 ), 16 );
			for (let j = 0; j < this.instancesLevels[i].count; j++) {
				this.children[i].setMatrixAt(j,this.instancesLevels[i].matrix4[j]);
			}
		}
	}
	
	setLevel(level) {
		if (level === -1) {
			this.manualLevelSet = false;
			this.autoUpdate = true;
		} else {
			let levelData = this.instancesLevels[level];
			if (!levelData) {
				levelData = this.instancesLevels[this.instancesLevels.length - 1];
			}
			this.manualLevelSet = true;
			this.autoUpdate = false;
			
			for (let i = 0; i < this.instancesLevels.length; i++) {
				this.instancesLevels[i].count = 0;
				this.instancesLevels[i].matrix4 = [];
			}
			
			for (let i = 0; i < this.matrices.length; i++) {
				levelData.count++;
				levelData.matrix4.push(new Matrix4().copy(this.matrices[i]))
			}
			
			for (let i = 0; i < this.instancesLevels.length; i++) {
				this.children[i].count = this.instancesLevels[i].count;
				this.children[i].instanceMatrix = new InstancedBufferAttribute( new Float32Array( this.instancesLevels[i].count * 16 ), 16 );
				for (let j = 0; j < this.instancesLevels[i].count; j++) {
					this.children[i].setMatrixAt(j,this.instancesLevels[i].matrix4[j]);
				}
			}
			
		}
	}
	
	static manualUpdate() {
		if (InstanceLOD.scopes.length) {
			InstanceLOD.scopes.forEach(scope => !scope.manualLevelSet && scope.update(threeJsVariables.perspectiveCamera));
		}
	}
}
